import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { verisureService } from '../../services/api';
import { localDb } from '../../services/localDb';
import { VerisureItem } from '../../types';
import { toast } from 'react-hot-toast';
import { LiveCameraModal } from '../../components/verisure/LiveCameraModal';

export const LogRepairPage: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>(); // This is actually the itemId
  const { user } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState<VerisureItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>(assetId || '');
  // const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [report, setReport] = useState('');
  const [partsChanged, setPartsChanged] = useState('');
  const [cost, setCost] = useState<number | ''>('');
  const [costCurrency, setCostCurrency] = useState('NGN');
  const [conditionScore, setConditionScore] = useState<number>(85);

  // Photo State
  const [faultyPhotos, setFaultyPhotos] = useState<string[]>([]);
  const [replacementPhotos, setReplacementPhotos] = useState<string[]>([]);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraType, setCameraType] = useState<'faulty' | 'replacement'>('faulty');

  useEffect(() => {
    // Fetch all items so user can select if no ID provided
    verisureService.getAllItems().then(async (response) => {
      let fetchedItems: VerisureItem[] = response.data || [];
      if (assetId) {
        const found = fetchedItems.find(i => i.id === assetId || i.assetId === assetId);
        if (!found) {
          try {
            const single = await verisureService.getItemById(assetId);
            if (single.data && single.data.make) {
              fetchedItems = [single.data, ...fetchedItems];
            }
          } catch {}
        }
        setSelectedItemId(found?.id || found?.assetId || assetId);
      }
      setItems(fetchedItems);
    });
  }, [assetId]);

  const openCamera = (type: 'faulty' | 'replacement') => {
    setCameraType(type);
    setCameraModalOpen(true);
  };

  const handlePhotoCaptured = (url: string) => {
    if (cameraType === 'faulty') {
      setFaultyPhotos((prev) => [...prev, url]);
    } else {
      setReplacementPhotos((prev) => [...prev, url]);
    }
    toast.success(`${cameraType === 'faulty' ? 'Faulty component' : 'Replacement component'} photo attached!`);
  };

  const handleRemovePhoto = (type: 'faulty' | 'replacement', index: number) => {
    if (type === 'faulty') {
      setFaultyPhotos((prev) => prev.filter((_, i) => i !== index));
    } else {
      setReplacementPhotos((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItemId) return toast.error('Please select an item');
    
    setSubmitting(true);
    try {
      const repairPayload = {
        itemId: selectedItemId,
        technicianId: user?.id || 'unknown',
        technicianName: user?.name || 'Unknown Tech',
        date: new Date().toISOString(),
        report,
        partsChanged,
        cost: Number(cost),
        costCurrency,
        conditionScore,
        faultyPartPhotos: faultyPhotos,
        replacementPartPhotos: replacementPhotos,
        gpsLat: 9.0765, // Mock GPS
        gpsLng: 7.3986
      };

      if (!navigator.onLine) {
        await localDb.queuedRepairs.add(repairPayload);
        toast.success('Offline mode: Repair saved locally! Will sync when connection is restored.', { icon: '📶' });
        navigate(`/item/${selectedItemId}`);
      } else {
        await verisureService.logRepair(repairPayload);
        toast.success('Immutable repair log successfully created!');
        navigate(`/item/${selectedItemId}`);
      }
    } catch (err: any) {
      toast.error('Failed to log repair: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="inline-block px-3 py-1 bg-gradient-to-r from-red-500/10 via-orange-500/15 to-amber-500/10 border border-orange-500/30 text-orange-400 text-[10px] font-bold tracking-widest uppercase rounded-full mb-3">
          Technician Protocol
        </div>
        <h1 className="text-3xl font-black text-white">Log Certified Repair</h1>
        <p className="text-slate-400 mt-1">This report is immutable and serves as the source of truth for insurance claims.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-8 shadow-xl">
        
        {/* Item Selection */}
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Select Item Being Repaired</label>
          <select 
            value={selectedItemId}
            onChange={(e) => setSelectedItemId(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition"
            required
          >
            <option value="" disabled>-- Select Registered Item --</option>
            {items.map(item => (
              <option key={item.id} value={item.id}>
                [{item.assetId}] {item.make} {item.model} (Owner: {item.ownerName})
              </option>
            ))}
          </select>
        </div>

        {/* Diagnosis & Report */}
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Diagnostic Report</label>
          <textarea 
            required
            rows={4}
            value={report}
            onChange={(e) => setReport(e.target.value)}
            placeholder="Describe the issue, cause, and what was done..."
            className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition"
          />
        </div>

        {/* Parts & Cost */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Parts Changed / Replaced</label>
            <textarea 
              required
              rows={3}
              value={partsChanged}
              onChange={(e) => setPartsChanged(e.target.value)}
              placeholder="List parts..."
              className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition"
            />
          </div>
          
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Cost of Repair</label>
              <div className="flex gap-2">
                <select 
                  value={costCurrency}
                  onChange={(e) => setCostCurrency(e.target.value)}
                  className="bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 w-24 focus:border-orange-500 focus:outline-none"
                >
                  <option value="NGN">₦ NGN</option>
                  <option value="USD">$ USD</option>
                </select>
                <input 
                  type="number" required
                  value={cost} onChange={(e) => setCost(Number(e.target.value))}
                  placeholder="e.g. 45000"
                  className="flex-1 bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">New Condition Score (0-100)</label>
              <input 
                type="number" min="0" max="100" required
                value={conditionScore} onChange={(e) => setConditionScore(Number(e.target.value))}
                className="w-full bg-slate-950 border border-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-orange-500 transition"
              />
            </div>
          </div>
        </div>

        {/* Photographic Evidence */}
        <div className="p-6 border border-slate-700 border-dashed rounded-2xl bg-slate-950/50">
          <h3 className="text-sm font-bold text-white mb-1">Photographic Evidence</h3>
          <p className="text-xs text-slate-400 mb-4">You must provide evidence of the faulty part and the newly installed replacement.</p>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <button 
                type="button" 
                onClick={() => openCamera('faulty')}
                className="w-full bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 rounded-xl py-3.5 font-bold text-sm mb-3 transition flex justify-center items-center gap-2 hover:border-orange-500/40 shadow-sm"
              >
                <span>📷</span> Open Camera: Faulty Part {faultyPhotos.length > 0 && <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full">({faultyPhotos.length})</span>}
              </button>
              {faultyPhotos.length > 0 ? (
                <div className="flex gap-3 overflow-x-auto py-1">
                  {faultyPhotos.map((url, i) => (
                    <div key={i} className="relative group flex-shrink-0">
                      <img key={i} src={url} alt="Faulty Evidence" className="w-16 h-16 rounded-xl object-cover border border-slate-700 shadow-md" />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto('faulty', i)}
                        className="absolute -top-1.5 -right-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow-md"
                        title="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-500 italic">No faulty part photos captured yet.</p>
              )}
            </div>
            
            <div>
              <button 
                type="button" 
                onClick={() => openCamera('replacement')}
                className="w-full bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 rounded-xl py-3.5 font-bold text-sm mb-3 transition flex justify-center items-center gap-2 hover:border-orange-500/40 shadow-sm"
              >
                <span>📷</span> Open Camera: Replacement {replacementPhotos.length > 0 && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">({replacementPhotos.length})</span>}
              </button>
              {replacementPhotos.length > 0 ? (
                <div className="flex gap-3 overflow-x-auto py-1">
                  {replacementPhotos.map((url, i) => (
                    <div key={i} className="relative group flex-shrink-0">
                      <img key={i} src={url} alt="Replacement Evidence" className="w-16 h-16 rounded-xl object-cover border border-slate-700 shadow-md" />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto('replacement', i)}
                        className="absolute -top-1.5 -right-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow-md"
                        title="Remove photo"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-slate-500 italic">No replacement photos captured yet.</p>
              )}
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          disabled={submitting}
          className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 disabled:bg-slate-600 text-white font-bold py-4 rounded-2xl text-lg shadow-lg shadow-orange-500/20 transition"
        >
          {submitting ? 'Logging...' : 'Submit Certified Repair Log'}
        </button>

      </form>

      {/* In-app Live Camera Modal */}
      <LiveCameraModal
        isOpen={cameraModalOpen}
        onClose={() => setCameraModalOpen(false)}
        onCapture={handlePhotoCaptured}
        title={cameraType === 'faulty' ? 'Capture Faulty Part Evidence' : 'Capture Replacement Part Evidence'}
        subtitle={
          cameraType === 'faulty'
            ? 'Align the damaged or defective part in the viewfinder before snapping the shot.'
            : 'Capture clear evidence of the newly installed replacement component.'
        }
      />
    </div>
  );
};
